import { useState } from "react";
import { Paintbrush, RotateCcw, X, Send, Lock } from "lucide-react";
import { toast } from "sonner";
import { useApi, errMsg } from "@/lib/api";
import { useSession } from "@/context/Session";
import { t } from "@/i18n";

const QUICK = ["Make the text bigger", "Switch to dark mode", "Use more spacing", "Make buttons easier to see"];

function PresetControls({ api, tokens }) {
  const [role, setRole] = useState("apprentice");
  const save = async () => {
    try {
      await api.post("/ui/presets", { role, tokens });
      toast.success(t("Saved current look as a shared preset"));
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex items-center gap-2 pt-3 border-t border-[var(--border-color)]">
      <span className="mono-label">{t("Shared preset")}</span>
      <select value={role} onChange={(e) => setRole(e.target.value)} className="field !py-1 !w-auto text-sm" data-testid="ui-preset-role-select">
        <option value="apprentice">{t("Apprentices")}</option>
        <option value="manager">{t("Managers")}</option>
        <option value="admin">{t("Owners")}</option>
      </select>
      <button className="btn btn-ghost btn-sm" onClick={save} data-testid="ui-preset-save-button">{t("Save")}</button>
    </div>
  );
}

export function UiAssistant() {
  const api = useApi();
  const { me, ui, setUiState } = useSession();
  const [open, setOpen] = useState(false);
  const [cmd, setCmd] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const run = async (text) => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/ui/command", { command: text });
      setUiState(data);
      setMsg(data.message);
      setCmd("");
    } catch (e) { setMsg(errMsg(e)); }
    setBusy(false);
  };
  const reset = async () => {
    try {
      const { data } = await api.delete("/ui/prefs");
      setUiState(data);
      setMsg(t("Back to the default look."));
    } catch (e) { setMsg(errMsg(e)); }
  };

  if (!open)
    return (
      <button onClick={() => setOpen(true)} title={t("Customize view")} className="w-8 h-8 sm:w-10 sm:h-10 rounded-full grid place-items-center faint hover:text-[var(--accent-primary)] hover:bg-[var(--bg-surface-hover)] transition-colors" data-testid="ui-assistant-open-button">
        <Paintbrush size={16} />
      </button>
    );
  return (
    <div className="card fixed top-[4.25rem] right-4 z-50 w-[min(24rem,calc(100vw-2rem))] shadow-2xl rise stack-sm" data-testid="ui-assistant-panel">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-semibold">{t("Customize view")}</p>
          <p className="text-xs faint flex items-center gap-1"><Lock size={11} /> {t("Look only. Knowledge stays the same.")}</p>
        </div>
        <button onClick={() => setOpen(false)} className="faint hover:text-[var(--text-primary)]" data-testid="ui-assistant-close-button"><X size={18} /></button>
      </div>
      <form onSubmit={(e) => { e.preventDefault(); run(cmd); }} className="flex gap-2">
        <input value={cmd} onChange={(e) => setCmd(e.target.value)} placeholder={t("e.g. make the text bigger")} className="field" disabled={busy} data-testid="ui-assistant-input" />
        <button className="btn btn-primary btn-sm" disabled={busy || !cmd.trim()} data-testid="ui-assistant-submit-button"><Send size={14} /></button>
      </form>
      <div className="flex flex-wrap gap-1.5">
        {QUICK.map((q) => (
          <button key={q} className="chip chip-muted hover:border-[var(--border-active)]" onClick={() => run(q)} disabled={busy} data-testid={`ui-quick-${q.split(" ").slice(-2).join("-").toLowerCase()}`}>{t(q)}</button>
        ))}
      </div>
      {busy && <p className="text-sm faint pulse-dot">{t("Interpreting…")}</p>}
      {msg && !busy && <p className="text-sm muted" data-testid="ui-assistant-message">{msg}</p>}
      <div className="flex items-center justify-between">
        <span className="mono-label" data-testid="ui-assistant-current">{t(ui.tokens.theme)} · {ui.tokens.fontScale}× {t("text")}</span>
        <button className="btn btn-ghost btn-sm" onClick={reset} data-testid="ui-assistant-reset-button"><RotateCcw size={14} /> {t("Reset to default")}</button>
      </div>
      {me.role === "admin" && <PresetControls api={api} tokens={ui.tokens} />}
    </div>
  );
}
